import React from 'react';

import type { IntlShape } from 'react-intl';

import { convertToInlineCss } from '@atlaskit/editor-common/lazy-node-view';
import type { PortalProviderAPI } from '@atlaskit/editor-common/portal';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import {
	PluginKey,
	type EditorState,
	type ReadonlyTransaction,
	type Transaction,
} from '@atlaskit/editor-prosemirror/state';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';

import type { AiSuggestionsPlugin, Suggestion } from '../aiSuggestionsPluginType';
import { SuggestionIconButton } from '../ui/components/SuggestionIconButton';
import { buildSuggestionMaps } from '../ui/utils/buildSuggestionMaps';
import { getAnchorLocalId } from '../ui/utils/getAnchorLocalId';
import { updateSuggestions } from '../ui/utils/updateSuggestions';

import { calculateHoverDecorations } from './hoverHighlightDecorations';
import {
	createRemixPlaceholderContainer,
	REMIX_PLACEHOLDER_WIDGET_KEY,
	RemixPlaceholder,
} from './remixPlaceholder';
import { getRemixWidgetPosition } from './remixWidgetPosition';
import { thinkingPluginKey } from './thinking-plugin';

/**
 * We have different adjustments for aligned headings vs. non-aligned headings because
 * they have different margins for some reason, see platform/packages/editor/editor-common/src/styles/shared/headings.ts
 *
 * This is just hard coded at the moment to mimic the positioning logic of block controls plugin, and won't be needed
 * once the icon button positioning logic is moved to a new block controls plugin API.
 */
const nodeTopAdjustments: Record<string, number> = {
	table: 12,
	paragraph: 2,
	heading1: 5,
	heading2: 2,
	heading3: -1,
	heading4: -3,
	heading5: -3,
	heading6: -3,
	heading1_aligned: 5,
	heading2_aligned: 2,
	heading3_aligned: -1,
	heading4_aligned: -3,
	heading5_aligned: -3,
	heading6_aligned: -3,
	layoutSection: 8,
};

/**
 * It would be easier to get the margin from the DOM but we don't have access to the view / DOM here
 * so we hard code these values for now, until everything's moved to the block controls plugin.
 */
const nodeTopMargins: Record<string, number> = {
	table: 12,
	paragraph: 12,
	bulletList: 12,
	orderedList: 12,
	decisionList: 8,
	taskList: 12,
	codeBlock: 12,
	panel: 12,
	rule: 4,
	heading1: 40,
	heading2: 32,
	heading3: 24,
	heading4: 20,
	heading5: 20,
	heading6: 20,
	heading1_aligned: 45,
	heading2_aligned: 41,
	heading3_aligned: 36,
	heading4_aligned: 21,
	heading5_aligned: 22,
	heading6_aligned: 18,
	blockquote: 12,
	expand: 4,
};

/**
 * Bottom margins of nodes that affect how the icon button is positioned
 */
const nodeBeforeAdjustments: Record<string, number> = {
	rule: 24,
	extension: 12,
};

const constructHeadingAdjustmentKey = (node: PMNode): string => {
	if (node.type.name === 'heading') {
		const level = node.attrs.level;
		const hasAlignment = node.marks.some((mark) => mark.type.name === 'alignment');
		return `heading${level}${hasAlignment ? '_aligned' : ''}`;
	}
	return node.type.name;
};

/**
 * If `pos` resolves to inside one (or more) `expand` / `nestedExpand` nodes,
 * return the doc-coordinate range of the *outermost* such ancestor. Used to
 * pin the suggestion card's anchor spans to the visible top/bottom of the
 * expand instead of to the deep inner target node — that way the card lines
 * up with the expand the user actually sees, not with content buried in its
 * (now revealed) body.
 */
function findOutermostExpandRange(
	doc: PMNode,
	pos: number,
): { endPos: number; startPos: number } | undefined {
	const $pos = doc.resolve(pos);
	let outermostExpandDepth: number | undefined;

	for (let depth = $pos.depth; depth > 0; depth--) {
		const node = $pos.node(depth);
		if (node.type.name === 'expand' || node.type.name === 'nestedExpand') {
			outermostExpandDepth = depth;
		}
	}

	if (outermostExpandDepth === undefined) {
		return undefined;
	}

	return {
		startPos: $pos.before(outermostExpandDepth),
		endPos: $pos.after(outermostExpandDepth),
	};
}

export type AiSuggestionsPluginState = {
	baseLocalIdBySuggestionId: Map<string, string>;
	/**
	 * The content (page) the current suggestions belong to. Used to detect page
	 * navigation: when a new suggestion set arrives for a different `contentAri`,
	 * any selection/diff from the previous page is reset.
	 */
	contentAri: string | undefined;
	decorations: DecorationSet;
	/**
	 * Blue highlight decorations for the suggestion whose icon is currently
	 * hovered. Kept separate from `decorations` (which carries the icon widgets)
	 * so hovering never rebuilds — and therefore never remounts — the icon
	 * portals. Merged with `decorations` in the plugin's `decorations` prop.
	 */
	hoverDecorations: DecorationSet;
	hoveredSuggestionId: string | undefined;
	/**
	 * The `singleInstrumentationID` of the current suggestion journey's originating
	 * `aiInteraction initiated` event (set when `startThinking` fires it). Persisted
	 * here so the `aiFeedback submitted` event can reuse it and correlate a user's
	 * thumbs feedback back to the interaction that produced the suggestions.
	 */
	interactionID: string | undefined;
	isHidden: boolean;
	/**
	 * `decorations` merged with `hoverDecorations`, precomputed in `apply` so the
	 * `decorations` prop can return a stable set rather than allocating a new
	 * merged `DecorationSet` on every ProseMirror update while a hover is active.
	 * Equals `decorations` by reference when there is no hover highlight.
	 */
	mergedDecorations: DecorationSet;
	orderedSuggestionIds: string[];
	selectedSuggestion: string | undefined;
	suggestionById: Map<string, Suggestion>;
	suggestionIdsByBaseLocalId: Map<string, Set<string>>;
};

export const aiSuggestionsPluginKey: PluginKey<AiSuggestionsPluginState> =
	new PluginKey<AiSuggestionsPluginState>('aiSuggestionsPlugin');

export type AiSuggestionsMeta =
	| { action: 'SET_SUGGESTIONS'; contentAri?: string; suggestions: Suggestion[] }
	| { action: 'SET_INTERACTION_ID'; interactionID: string }
	| { action: 'CLEAR_SUGGESTIONS' }
	| { action: 'SELECT_SUGGESTION'; id: string | undefined }
	| { action: 'REMOVE_SUGGESTION'; id: string }
	| { action: 'SET_UI_VISIBILITY'; isHidden: boolean }
	| { action: 'HOVER_SUGGESTION'; id: string | undefined };

type DocRange = {
	from: number;
	to: number;
};

const pointTouchesRange = (point: number, range: DocRange): boolean =>
	point >= range.from && point <= range.to;

const rangesOverlap = (rangeA: DocRange, rangeB: DocRange): boolean => {
	if (rangeA.from === rangeA.to) {
		return pointTouchesRange(rangeA.from, rangeB);
	}

	if (rangeB.from === rangeB.to) {
		return pointTouchesRange(rangeB.from, rangeA);
	}

	return rangeA.from < rangeB.to && rangeB.from < rangeA.to;
};

const getDecoratedNodeRanges = (
	doc: PMNode,
	suggestionIdsByBaseLocalId: Map<string, Set<string>>,
): DocRange[] => {
	const decoratedNodeRanges: DocRange[] = [];

	doc.descendants((node, pos) => {
		if (suggestionIdsByBaseLocalId.has(node.attrs?.localId)) {
			decoratedNodeRanges.push({ from: pos, to: pos + node.nodeSize });
			return false;
		}

		return true;
	});

	return decoratedNodeRanges;
};

const docChangeAffectsDecorations = ({
	doc,
	suggestionIdsByBaseLocalId,
	tr,
}: {
	doc: PMNode;
	suggestionIdsByBaseLocalId: Map<string, Set<string>>;
	tr: ReadonlyTransaction;
}): boolean => {
	if (!tr.docChanged || suggestionIdsByBaseLocalId.size === 0) {
		return false;
	}

	const decoratedNodeRanges = getDecoratedNodeRanges(doc, suggestionIdsByBaseLocalId);
	if (decoratedNodeRanges.length === 0) {
		return false;
	}

	return tr.mapping.maps.some((stepMap) => {
		let affectsDecorations = false;

		stepMap.forEach((oldStart, oldEnd) => {
			if (affectsDecorations) {
				return;
			}

			const changedRange = { from: oldStart, to: oldEnd };
			affectsDecorations = decoratedNodeRanges.some((decoratedRange) =>
				rangesOverlap(changedRange, decoratedRange),
			);
		});

		return affectsDecorations;
	});
};

function calculateSuggestionsDecorations({
	doc,
	suggestionById,
	suggestionIdsByBaseLocalId,
	api,
	intl,
	selectedSuggestion,
	nodeViewPortalProviderAPI,
}: {
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined;
	doc: PMNode;
	intl: IntlShape | undefined;
	nodeViewPortalProviderAPI: PortalProviderAPI;
	selectedSuggestion: string | undefined;
	suggestionById: Map<string, Suggestion>;
	suggestionIdsByBaseLocalId: Map<string, Set<string>>;
}): DecorationSet {
	if (suggestionById.size === 0) {
		return DecorationSet.empty;
	}

	const decorations: Decoration[] = [];

	doc.descendants((node, pos) => {
		const { localId } = node.attrs;
		if (localId) {
			const suggestionsAtLocalId = suggestionIdsByBaseLocalId.get(localId);
			if (suggestionsAtLocalId?.size) {
				const firstSuggestionId = suggestionsAtLocalId.values().next().value;
				const firstSuggestion = firstSuggestionId
					? suggestionById.get(firstSuggestionId)
					: undefined;

				if (firstSuggestion) {
					const element = document.createElement('span');
					const nodeTypeName =
						node.type.name === 'heading' ? constructHeadingAdjustmentKey(node) : node.type.name;
					element.setAttribute(
						'style',
						convertToInlineCss({
							position: 'relative',
							width: '100%',
							display: 'block',
							pointerEvents: 'none',
						}),
					);

					const key = crypto.randomUUID();
					const isSelected = Boolean(
						selectedSuggestion && suggestionsAtLocalId.has(selectedSuggestion),
					);

					const isTableNode = node.type.name === 'table';

					// Tables don't use breakout for resizing, the width is stored in the attrs
					// but will be clamped by the max content width (100cqw)
					// The (-4px) is mimicking spacing of the remix icon button in block controls plugin
					const leftAdjustment =
						isTableNode && typeof node.attrs?.width === 'number'
							? `calc(((min(100cqw - (72px * 2), ${node.attrs.width}px) - 760px) / 2) - 4px)`
							: undefined;

					const nodeBefore = doc.resolve(pos).nodeBefore;
					const isBlockLeafNodeBefore = nodeBefore?.isBlock && nodeBefore.isLeaf;

					const topAdjustment = nodeTopAdjustments[nodeTypeName] ?? 0;
					const topMargin = nodeTopMargins[nodeTypeName] ?? 0;
					const nodeBeforeAdjustment = nodeBeforeAdjustments[nodeBefore?.type.name ?? ''] ?? 0;

					const calculatedTopAdjustment =
						(pos === 0
							? isTableNode
								? topAdjustment
								: 0
							: isBlockLeafNodeBefore
								? Math.max(0, topMargin - nodeBeforeAdjustment)
								: topMargin) + topAdjustment;

					decorations.push(
						Decoration.widget(
							pos,
							() => {
								nodeViewPortalProviderAPI.render(
									() => (
										<SuggestionIconButton
											leftAdjustment={leftAdjustment}
											topAdjustment={calculatedTopAdjustment}
											suggestion={firstSuggestion}
											suggestionsCount={suggestionsAtLocalId?.size}
											isSelected={isSelected}
											api={api}
											intl={intl}
										/>
									),
									element,
									key,
								);
								return element;
							},
							{
								side: -2,
								key,
								marks: node.marks.filter((mark) => mark.type.name === 'breakout'),
								destroy: () => {
									// Clean up properly
									// nodeViewPortalProviderAPI.remove(key);
								},
							},
						),
					);

					if (isSelected) {
						const selectedSuggestionData = selectedSuggestion
							? suggestionById.get(selectedSuggestion)
							: undefined;
						const anchorLocalId = selectedSuggestionData
							? getAnchorLocalId(selectedSuggestionData)
							: undefined;

						// While a SUGGESTED_REMIX card is open, drop a placeholder block at the
						// insertion point so the user can see where the generated result will land.
						// The engine takes over with its own thinking bar + inserted content once
						// the suggestion is accepted (and this placeholder is removed with the card).
						if (anchorLocalId && selectedSuggestionData?.actionKind === 'SUGGESTED_REMIX') {
							const placeholderPos = getRemixWidgetPosition(doc, anchorLocalId);
							if (placeholderPos !== undefined) {
								const placeholderElement = createRemixPlaceholderContainer();
								// No `destroy`; the portal is removed manually when the card goes inactive (see plugin `view`).
								decorations.push(
									Decoration.widget(
										placeholderPos,
										() => {
											nodeViewPortalProviderAPI.render(
												() => <RemixPlaceholder />,
												placeholderElement,
												REMIX_PLACEHOLDER_WIDGET_KEY,
											);
											return placeholderElement;
										},
										{
											key: REMIX_PLACEHOLDER_WIDGET_KEY,
											side: -1,
										},
									),
								);
							}
						}

						if (anchorLocalId) {
							let anchorNodePosition: { nodeSize: number; pos: number } | undefined;
							if (anchorLocalId === localId) {
								anchorNodePosition = { pos, nodeSize: node.nodeSize };
							} else {
								node.descendants((descendant, descendantPos) => {
									if (anchorNodePosition) {
										return false;
									}
									const descendantLocalId = descendant.attrs?.localId;
									if (descendantLocalId === anchorLocalId) {
										anchorNodePosition = {
											pos: pos + descendantPos + 1,
											nodeSize: descendant.nodeSize,
										};
										return false;
									}
									return true;
								});
							}

							const innerCardStartPos = anchorNodePosition?.pos ?? pos;
							const innerCardEndPos = anchorNodePosition
								? anchorNodePosition.pos + anchorNodePosition.nodeSize
								: pos + node.nodeSize;

							// If the target sits inside an `expand` / `nestedExpand`, pin the card
							// to the *outermost* expand's boundaries instead. The icon button is
							// rendered on the doc-level (depth 1) baseLocalId block — i.e. on the
							// expand itself — so the user clicked at the top of the expand and
							// expects the card to open next to that, not deep inside the (now
							// revealed) expand body where the actual tool call target lives.
							const expandRange = findOutermostExpandRange(doc, innerCardStartPos);
							const cardStartPos = expandRange?.startPos ?? innerCardStartPos;
							const cardEndPos = expandRange?.endPos ?? innerCardEndPos;

							/**
							 * These decoration widgets are used to track the top and bottom of the localId,
							 * also accounting for other decoration widgets (such as diffs).
							 */
							decorations.push(
								Decoration.widget(
									cardStartPos,
									() => {
										const spanElement = document.createElement('span');
										spanElement.setAttribute(
											'style',
											convertToInlineCss({
												anchorName: `--card-${anchorLocalId}-start`,
											}),
										);
										return spanElement;
									},
									{
										side: -2,
									},
								),
							);

							decorations.push(
								Decoration.widget(
									cardEndPos,
									() => {
										const spanElement = document.createElement('span');
										spanElement.setAttribute(
											'style',
											convertToInlineCss({
												anchorName: `--card-${anchorLocalId}-end`,
											}),
										);
										return spanElement;
									},
									{
										side: 2,
									},
								),
							);
						}
					}
				}
			}
		}
		return false;
	});

	if (!decorations.length) {
		return DecorationSet.empty;
	}

	return DecorationSet.create(doc, decorations);
}

// Whether a `SUGGESTED_REMIX` card is selected; gates the manual placeholder-portal teardown.
function isSuggestedRemixCardActive(pluginState: AiSuggestionsPluginState | undefined): boolean {
	if (!pluginState?.selectedSuggestion) {
		return false;
	}
	return (
		pluginState.suggestionById.get(pluginState.selectedSuggestion)?.actionKind === 'SUGGESTED_REMIX'
	);
}

const {
	suggestionById: initSuggestionById,
	suggestionIdsByBaseLocalId: initSuggestionIdsByBaseLocalId,
	baseLocalIdBySuggestionId: initBaseLocalIdBySuggestionId,
	orderedSuggestionIds: initOrderedSuggestionIds,
} = buildSuggestionMaps(undefined);

const initState: AiSuggestionsPluginState = {
	contentAri: undefined,
	decorations: DecorationSet.empty,
	hoverDecorations: DecorationSet.empty,
	hoveredSuggestionId: undefined,
	interactionID: undefined,
	mergedDecorations: DecorationSet.empty,
	isHidden: false,
	selectedSuggestion: undefined,
	suggestionById: initSuggestionById,
	suggestionIdsByBaseLocalId: initSuggestionIdsByBaseLocalId,
	baseLocalIdBySuggestionId: initBaseLocalIdBySuggestionId,
	orderedSuggestionIds: initOrderedSuggestionIds,
};

export function createPlugin({
	api,
	intl,
	contentAri,
	nodeViewPortalProviderAPI,
}: {
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
	contentAri?: string;
	intl?: IntlShape;
	nodeViewPortalProviderAPI: PortalProviderAPI;
}): SafePlugin<AiSuggestionsPluginState> {
	return new SafePlugin<AiSuggestionsPluginState>({
		key: aiSuggestionsPluginKey,
		state: {
			init(_config, _state: EditorState): AiSuggestionsPluginState {
				return initState;
			},
			apply(
				tr: ReadonlyTransaction,
				pluginState: AiSuggestionsPluginState,
				_oldState: EditorState,
				newState: EditorState,
			): AiSuggestionsPluginState {
				const meta = tr.getMeta(aiSuggestionsPluginKey) as AiSuggestionsMeta | undefined;
				let nextState = pluginState;

				if (meta) {
					switch (meta.action) {
						case 'SET_SUGGESTIONS': {
							const {
								suggestionById,
								baseLocalIdBySuggestionId,
								suggestionIdsByBaseLocalId,
								orderedSuggestionIds,
							} = buildSuggestionMaps({
								suggestions: meta.suggestions,
								doc: newState.doc,
							});

							// Track the content these suggestions belong to. Prefer the explicit
							// `contentAri` on the meta, falling back to the suggestions' own
							// `contentAri` so direct dispatchers are covered too. `||` (not `??`)
							// is intentional so an empty-string `contentAri` is treated as "not
							// provided" rather than a distinct page (which would cause a spurious
							// reset against a previously-tracked ARI).
							const incomingContentAri = meta.contentAri || meta.suggestions[0]?.contentAri;
							// When the suggestions belong to a different page than the one currently
							// tracked, the selection (and its diff) is stale — reset it. Suggestions
							// refreshed for the *same* page keep the existing selection.
							const contentChanged =
								incomingContentAri !== undefined &&
								pluginState.contentAri !== undefined &&
								incomingContentAri !== pluginState.contentAri;
							const nextSelectedSuggestion = contentChanged
								? undefined
								: pluginState.selectedSuggestion;

							nextState = {
								...pluginState,
								contentAri: incomingContentAri ?? pluginState.contentAri,
								selectedSuggestion: nextSelectedSuggestion,
								suggestionById,
								suggestionIdsByBaseLocalId,
								baseLocalIdBySuggestionId,
								orderedSuggestionIds,
								hoverDecorations: DecorationSet.empty,
								hoveredSuggestionId: undefined,
								decorations: pluginState.isHidden
									? DecorationSet.empty
									: calculateSuggestionsDecorations({
											doc: newState.doc,
											suggestionById,
											suggestionIdsByBaseLocalId,
											api,
											intl,
											selectedSuggestion: nextSelectedSuggestion,
											nodeViewPortalProviderAPI,
										}),
							};
							break;
						}
						case 'CLEAR_SUGGESTIONS': {
							const {
								suggestionById,
								baseLocalIdBySuggestionId,
								suggestionIdsByBaseLocalId,
								orderedSuggestionIds,
							} = buildSuggestionMaps({
								doc: newState.doc,
								suggestions: [],
							});
							nextState = {
								...pluginState,
								suggestionById,
								suggestionIdsByBaseLocalId,
								baseLocalIdBySuggestionId,
								orderedSuggestionIds,
								selectedSuggestion: undefined,
								decorations: DecorationSet.empty,
								hoverDecorations: DecorationSet.empty,
								hoveredSuggestionId: undefined,
							};
							break;
						}
						case 'SET_INTERACTION_ID': {
							// Persist the originating interaction's instrumentation ID so
							// feedback events can correlate back to the `aiInteraction initiated`
							// event for this suggestion journey.
							nextState = {
								...pluginState,
								interactionID: meta.interactionID,
							};
							break;
						}
						case 'SELECT_SUGGESTION':
							nextState = {
								...pluginState,
								selectedSuggestion: meta.id,
								decorations: pluginState.isHidden
									? DecorationSet.empty
									: calculateSuggestionsDecorations({
											doc: newState.doc,
											suggestionById: pluginState.suggestionById,
											suggestionIdsByBaseLocalId: pluginState.suggestionIdsByBaseLocalId,
											api,
											intl,
											selectedSuggestion: meta.id,
											nodeViewPortalProviderAPI,
										}),
							};
							break;
						case 'SET_UI_VISIBILITY': {
							nextState = {
								...pluginState,
								isHidden: meta.isHidden,
								hoverDecorations: meta.isHidden
									? DecorationSet.empty
									: pluginState.hoverDecorations,
								hoveredSuggestionId: meta.isHidden ? undefined : pluginState.hoveredSuggestionId,
								decorations: meta.isHidden
									? DecorationSet.empty
									: calculateSuggestionsDecorations({
											doc: newState.doc,
											suggestionById: pluginState.suggestionById,
											suggestionIdsByBaseLocalId: pluginState.suggestionIdsByBaseLocalId,
											api,
											intl,
											selectedSuggestion: pluginState.selectedSuggestion,
											nodeViewPortalProviderAPI,
										}),
							};
							break;
						}
						case 'REMOVE_SUGGESTION': {
							const {
								suggestionById,
								baseLocalIdBySuggestionId,
								suggestionIdsByBaseLocalId,
								orderedSuggestionIds,
							} = pluginState;
							const removedId = meta.id;
							const suggestion = suggestionById.get(removedId);

							if (!suggestion) {
								nextState = {
									...pluginState,
									decorations: calculateSuggestionsDecorations({
										doc: newState.doc,
										suggestionById,
										suggestionIdsByBaseLocalId,
										api,
										intl,
										selectedSuggestion: undefined,
										nodeViewPortalProviderAPI,
									}),
								};
								break;
							}

							suggestionById.delete(removedId);

							const baseLocalId = baseLocalIdBySuggestionId.get(removedId);
							baseLocalIdBySuggestionId.delete(removedId);

							if (baseLocalId) {
								suggestionIdsByBaseLocalId.get(baseLocalId)?.delete(removedId);
							}

							nextState = {
								...pluginState,
								suggestionById,
								baseLocalIdBySuggestionId,
								suggestionIdsByBaseLocalId,
								orderedSuggestionIds: orderedSuggestionIds.filter((id) => id !== removedId),
								selectedSuggestion: undefined,
								decorations: calculateSuggestionsDecorations({
									doc: newState.doc,
									suggestionById,
									suggestionIdsByBaseLocalId,
									api,
									intl,
									selectedSuggestion: undefined,
									nodeViewPortalProviderAPI,
								}),
							};
							break;
						}
						case 'HOVER_SUGGESTION': {
							const hoveredSuggestion = meta.id
								? pluginState.suggestionById.get(meta.id)
								: undefined;
							nextState = {
								...pluginState,
								hoveredSuggestionId: meta.id,
								hoverDecorations: pluginState.isHidden
									? DecorationSet.empty
									: calculateHoverDecorations(newState.doc, hoveredSuggestion),
							};
							break;
						}
						default:
							break;
					}
				} else {
					const shouldRecalculateDecorations = docChangeAffectsDecorations({
						doc: _oldState.doc,
						suggestionIdsByBaseLocalId: pluginState.suggestionIdsByBaseLocalId,
						tr,
					});

					nextState = {
						...pluginState,
						hoverDecorations:
							pluginState.isHidden || pluginState.hoverDecorations === DecorationSet.empty
								? DecorationSet.empty
								: pluginState.hoverDecorations.map(tr.mapping, tr.doc),
						decorations: pluginState.isHidden
							? DecorationSet.empty
							: shouldRecalculateDecorations
								? calculateSuggestionsDecorations({
										doc: newState.doc,
										suggestionById: pluginState.suggestionById,
										suggestionIdsByBaseLocalId: pluginState.suggestionIdsByBaseLocalId,
										api,
										intl,
										selectedSuggestion: pluginState.selectedSuggestion,
										nodeViewPortalProviderAPI,
									})
								: pluginState.decorations.map(tr.mapping, tr.doc),
					};
				}

				if (nextState === pluginState) {
					return nextState;
				}

				// Merge the icon-widget decorations with the hover highlight once here
				// so the `decorations` prop can return a stable set instead of
				// allocating a new merged DecorationSet on every ProseMirror update.
				// Reuse the `decorations` reference (no allocation) when nothing is
				// hovered.
				return {
					...nextState,
					mergedDecorations:
						nextState.hoverDecorations === DecorationSet.empty
							? nextState.decorations
							: nextState.decorations.add(newState.doc, nextState.hoverDecorations.find()),
				};
			},
		},
		/**
		 * The diff lives in the (separate) show-diff plugin, so it can't be reset from
		 * this plugin's `apply`. When the tracked `contentAri` changes — i.e. the
		 * suggestions now belong to a different page than the one whose diff is on
		 * screen — hide the diff so a stale diff doesn't linger on the new page (which
		 * can otherwise re-render whenever the new page contains a node with the same
		 * `localId`). The matching selection/decoration reset happens in the
		 * `SET_SUGGESTIONS` handler above.
		 */
		appendTransaction(
			_transactions: readonly Transaction[],
			oldState: EditorState,
			newState: EditorState,
		): Transaction | null {
			const previousContentAri = aiSuggestionsPluginKey.getState(oldState)?.contentAri;
			const nextContentAri = aiSuggestionsPluginKey.getState(newState)?.contentAri;
			const contentChanged =
				previousContentAri !== undefined &&
				nextContentAri !== undefined &&
				previousContentAri !== nextContentAri;
			if (!contentChanged) {
				return null;
			}

			const { tr } = newState;
			api?.showDiff?.commands.hideDiff?.({ tr });
			tr.setMeta(thinkingPluginKey, { status: 'idle' });
			return tr;
		},
		props: {
			decorations(state: EditorState) {
				const pluginState = aiSuggestionsPluginKey.getState(state);
				return pluginState?.mergedDecorations ?? DecorationSet.empty;
			},
		},
		view: () => {
			const abortController = new AbortController();
			if (contentAri && api) {
				updateSuggestions({ contentAri, api, signal: abortController.signal });
			}
			return {
				update: (view, prevState) => {
					// Remove the remix placeholder portal once, on the active → inactive transition.
					const wasActive = isSuggestedRemixCardActive(aiSuggestionsPluginKey.getState(prevState));
					const isActive = isSuggestedRemixCardActive(aiSuggestionsPluginKey.getState(view.state));
					if (wasActive && !isActive) {
						nodeViewPortalProviderAPI.remove(REMIX_PLACEHOLDER_WIDGET_KEY);
					}
				},
				destroy: () => {
					abortController.abort();
					nodeViewPortalProviderAPI.remove(REMIX_PLACEHOLDER_WIDGET_KEY);
				},
			};
		},
	});
}
